import {
  Scenario,
  type Area,
  type LatLon,
  type Scenario as ScenarioType,
} from "@radar-sim/schema";

/**
 * Wrap a tap-to-record polyline into a complete, validated race-fleet
 * scenario that can be saved to the user's library and re-launched.
 *
 * The course feeds into a `race-fleet` behaviour shared by every
 * yacht in the fleet; each yacht differs only in its starting
 * `progress` along the course (evenly staggered) and a small
 * alternating `lateralOffsetNm` so they don't render as a single
 * point. Own ship is placed inside the start of the course so the
 * student watches the fleet sail past from a stationary position
 * unless they pick a manual position.
 */
export interface BuildScenarioInput {
  name: string;
  area: Area;
  course: ReadonlyArray<LatLon>;
  fleetSize: number;
  speedKnots?: number;
  ownStart?: LatLon;
}

export type BuildResult =
  | { ok: true; scenario: ScenarioType; json: string }
  | { ok: false; error: string };

const DEFAULT_SPEED_KN = 6;
const DEFAULT_DURATION_MIN = 30;

export function buildScenarioFromCourse(input: BuildScenarioInput): BuildResult {
  if (input.course.length < 2) {
    return { ok: false, error: "Course needs at least two points." };
  }
  if (input.fleetSize < 1 || input.fleetSize > 40) {
    return { ok: false, error: "Fleet size must be between 1 and 40." };
  }
  const speed = input.speedKnots ?? DEFAULT_SPEED_KN;
  const id = makeId(input.name);
  const now = new Date().toISOString();
  const start = input.course[0]!;
  const second = input.course[1]!;
  const initialCog = bearingDegFlat(start, second);
  const ownShip = {
    position: input.ownStart ?? offsetPerpendicular(start, second, 0.3),
    cog: initialCog,
    sog: 0,
    heading: initialCog,
  };
  const vessels = buildFleet(input.fleetSize, input.course, speed);

  const scenario: unknown = {
    schemaVersion: 1,
    metadata: {
      id,
      name: input.name,
      description: `User-recorded ${input.fleetSize}-yacht race-fleet scenario.`,
      syllabusTags: ["g34.collision-avoidance.complex-traffic"],
      area: input.area,
      difficulty: 3,
      expectedDurationMinutes: DEFAULT_DURATION_MIN,
      author: "user",
      scenarioVersion: "0.1.0",
      createdAt: now,
      updatedAt: now,
    },
    initialConditions: {
      ownShip,
      simStartWallClock: roundedHourIso(now),
      environment: {
        seaState: 2,
        windFromDeg: 230,
        windSpeedKnots: 12,
        visibilityNm: 8,
        rainCells: [],
        tidePhase: "mid",
      },
    },
    vessels,
    events: [],
    successCriteria: [
      { kind: "complete_within", seconds: DEFAULT_DURATION_MIN * 60 },
    ],
  };

  const parsed = Scenario.safeParse(scenario);
  if (!parsed.success) {
    return { ok: false, error: parsed.error.issues[0]?.message ?? "invalid" };
  }
  return {
    ok: true,
    scenario: parsed.data,
    json: JSON.stringify(parsed.data, null, 2),
  };
}

function buildFleet(
  fleetSize: number,
  course: ReadonlyArray<LatLon>,
  speedKnots: number,
): ReadonlyArray<unknown> {
  const out: unknown[] = [];
  for (let i = 0; i < fleetSize; i++) {
    const progress = fleetSize === 1 ? 0 : i / fleetSize;
    // Alternate ±0.05 nm of scatter so the fleet renders as a band,
    // not a line. Yachts with progress 0 spawn exactly on the course
    // start, which is fine.
    const lateralOffsetNm = (i % 2 === 0 ? 1 : -1) * 0.04 * (1 + (i % 3) * 0.3);
    const seq = (i + 1).toString().padStart(2, "0");
    const spawnPoint = course[0]!;
    const next = course[Math.min(1, course.length - 1)]!;
    out.push({
      kind: "behavioural",
      id: `rf-yacht-${seq}`,
      vesselType: "yacht-sail",
      rcs: 0.2,
      ais: {
        mmsi: padMmsi(235100000 + i),
        name: `RF-YACHT-${seq}`,
        class: "B",
      },
      spawn: {
        position: spawnPoint,
        cog: bearingDegFlat(spawnPoint, next),
        sog: speedKnots,
      },
      behaviour: {
        name: "race-fleet",
        course: [...course],
        speedKnots,
        progress,
        lateralOffsetNm,
      },
    });
  }
  return out;
}

function makeId(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  const stamp = Date.now().toString(36);
  const base = slug.length > 0 ? slug : "course";
  return `user-${base}-${stamp}`;
}

function padMmsi(n: number): string {
  return n.toString().padStart(9, "0").slice(-9);
}

function roundedHourIso(nowIso: string): string {
  const d = new Date(nowIso);
  d.setMinutes(0, 0, 0);
  return d.toISOString();
}

/** Approximate bearing for short-range, used only for own-ship initial
 *  cog. Matches the geodesy library's flat-plane intent without
 *  pulling the engine into the web bundle's build graph. */
function bearingDegFlat(from: LatLon, to: LatLon): number {
  const dLon = (to.lon - from.lon) * Math.cos((from.lat * Math.PI) / 180);
  const dLat = to.lat - from.lat;
  const rad = Math.atan2(dLon, dLat);
  return ((rad * 180) / Math.PI + 360) % 360;
}

/** Place own ship `nm` to starboard of the first course leg so the
 *  fleet sails past, not at, the student. */
function offsetPerpendicular(
  start: LatLon,
  next: LatLon,
  nm: number,
): LatLon {
  const brg = bearingDegFlat(start, next);
  const stbd = (brg + 90) % 360;
  const rad = (stbd * Math.PI) / 180;
  // 1 nm ≈ 1/60° lat; lon scales by cos(lat).
  const dLat = (Math.cos(rad) * nm) / 60;
  const dLon =
    (Math.sin(rad) * nm) /
    (60 * Math.cos((start.lat * Math.PI) / 180));
  return { lat: start.lat + dLat, lon: start.lon + dLon };
}
